/**
 * Register view — F10/F11 proof surface.
 *
 * Upgraded from V5 basic register to incorporate V1 scaffold lineage:
 *   - Coverage meter (% with clocks)
 *   - Causal spine with lamport ordering
 *   - Link graph (object → object edges with types)
 *   - Schema-driven object rendering (enums as badges, arrays, nested)
 *   - Receipt stream with validation status
 *
 * Allen & Co aesthetic: white paper, black ink, editorial evidence.
 */

import { useState, useEffect } from "react";
import { getClients, applyMembrane } from "../lib/api-clients";

interface Receipt {
  receipt_id: string;
  settlement_state?: string;
  authority?: string;
  validation?: Array<{ check_id: string; status: string }>;
  lamport?: number;
  local_sequence?: number;
  source_ordinal?: number;
  label?: string;
  type?: string;
  state?: string;
}

interface RegisterObject {
  object_id: string;
  version?: number;
  status?: string;
  [key: string]: any;
}

interface Link {
  from_object_version_id: string;
  to_object_version_id: string;
  link_type: string;
  active: boolean;
}

interface SpineEntry {
  source_ordinal: number;
  lamport: number;
  label: string;
  state: string;
}

type Tab = "receipts" | "objects" | "links" | "spine" | "adjudication";

export function RegisterView({ isOperator }: { isOperator: boolean }) {
  const [tab, setTab] = useState<Tab>("receipts");
  const [data, setData] = useState<any>(null);
  const [objects, setObjects] = useState<RegisterObject[]>([]);
  const [links, setLinks] = useState<Link[]>([]);
  const [spine, setSpine] = useState<SpineEntry[]>([]);
  const [f4Verdicts, setF4Verdicts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const membraneBypass = isOperator;

  useEffect(() => {
    const clients = getClients();
    if (!clients) { setLoading(false); return; }

    // Fetch F4 verdicts
    clients.control.getHermesF4Verdicts()
      .then((res: any) => setF4Verdicts(res?.verdicts || []))
      .catch(() => {});

    Promise.allSettled([
      clients.control.getRegister(),
      clients.control.getRegisterObjects(),
      clients.control.getCausalSpine(),
    ]).then(([reg, objs, sp]) => {
      if (reg.status === "fulfilled") {
        const d = reg.value as any;
        setData(d);
        setLinks(d?.register?.links || d?.links || []);
      }
      if (objs.status === "fulfilled") {
        setObjects((objs.value as any)?.objects || []);
      }
      if (sp.status === "fulfilled") {
        setSpine((sp.value as any)?.spine || []);
      }
      setError(null);
    }).catch((e) => setError(e.message)).finally(() => setLoading(false));
  }, [isOperator]);

  if (loading) return <div className="loading">{applyMembrane("Loading register…", membraneBypass)}</div>;
  if (error) return <div className="error-state">{error}</div>;

  const receipts: Receipt[] = data?.register?.receipts || data?.receipts || [];
  const clocks = data?.register?.clocks || data?.clocks || [];
  const clockMap = new Map<string, any>(clocks.map((c: any) => [c.receipt_id, c]));
  const withClock = receipts.filter((r) => clockMap.has(r.receipt_id)).length;
  const pct = receipts.length > 0 ? Math.round((100 * withClock) / receipts.length) : 0;
  const coverage = data?.coverage ?? 0;

  return (
    <div className="view">
      <div className="view-header">
        <span className="eyebrow">{applyMembrane("Proof", membraneBypass)}</span>
        <h1>{applyMembrane("Register", membraneBypass)}</h1>
      </div>

      {/* Coverage meter */}
      <div className="coverage-meter">
        <div className="coverage-bar">
          <div className="coverage-fill" style={{ width: `${Math.max(pct, coverage)}%` }} />
        </div>
        <span className="coverage-text">
          {withClock}/{receipts.length} {applyMembrane("receipts have clocks", membraneBypass)} ({Math.max(pct, coverage)}%)
        </span>
      </div>

      {/* Stats row */}
      <div className="register-stats">
        <div className="stat-card">
          <div className="stat-label">{applyMembrane("Receipts", membraneBypass)}</div>
          <div className="stat-value">{receipts.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">{applyMembrane("Objects", membraneBypass)}</div>
          <div className="stat-value">{objects.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">{applyMembrane("Links", membraneBypass)}</div>
          <div className="stat-value">{links.length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">{applyMembrane("Coverage", membraneBypass)}</div>
          <div className="stat-value">{Math.max(pct, coverage)}%</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="register-tabs">
        <button className={`tab ${tab === "receipts" ? "active" : ""}`} onClick={() => setTab("receipts")}>
          {applyMembrane("Receipts", membraneBypass)} ({receipts.length})
        </button>
        <button className={`tab ${tab === "objects" ? "active" : ""}`} onClick={() => setTab("objects")}>
          {applyMembrane("Objects", membraneBypass)} ({objects.length})
        </button>
        <button className={`tab ${tab === "links" ? "active" : ""}`} onClick={() => setTab("links")}>
          {applyMembrane("Links", membraneBypass)} ({links.length})
        </button>
        <button className={`tab ${tab === "spine" ? "active" : ""}`} onClick={() => setTab("spine")}>
          {applyMembrane("Causal Spine", membraneBypass)}
        </button>
        <button className={`tab ${tab === "adjudication" ? "active" : ""}`} onClick={() => setTab("adjudication")}>
          {applyMembrane("Adjudication", membraneBypass)} ({f4Verdicts.length})
        </button>
      </div>

      {/* Receipts tab */}
      {tab === "receipts" && (
        <div className="receipt-list">
          {receipts.length === 0 ? (
            <div className="empty-state">
              {applyMembrane("No receipts yet. Receipts are append-only proof binding actor, authority, action, validation, evidence, and settlement.", membraneBypass)}
            </div>
          ) : (
            receipts.map((receipt) => {
              const clock = clockMap.get(receipt.receipt_id);
              return (
                <div key={receipt.receipt_id} className="card receipt-card">
                  <div className="card-title">{receipt.receipt_id}</div>
                  <div className="receipt-meta">
                    {receipt.settlement_state && (
                      <span className="badge" data-status={receipt.settlement_state}>
                        {receipt.settlement_state}
                      </span>
                    )}
                    {receipt.authority && (
                      <span className="badge">{applyMembrane(receipt.authority, membraneBypass)}</span>
                    )}
                  </div>
                  {receipt.validation && receipt.validation.length > 0 && (
                    <div className="receipt-validation">
                      {receipt.validation.map((v, i) => (
                        <span key={i} className="badge" data-status={v.status.toLowerCase()}>
                          {v.check_id}: {v.status}
                        </span>
                      ))}
                    </div>
                  )}
                  {clock && (
                    <span className="badge clock-badge">
                      clock: L{clock.lamport} S{clock.local_sequence}
                    </span>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Objects tab — schema-driven rendering (V1 pattern) */}
      {tab === "objects" && (
        <div className="object-list">
          {objects.length === 0 ? (
            <div className="empty-state">{applyMembrane("No objects in the register.", membraneBypass)}</div>
          ) : (
            objects.map((obj) => (
              <div key={obj.object_id} className="card object-card">
                <div className="object-card-header">
                  <div className="card-title">{obj.object_id}</div>
                  {obj.version !== undefined && (
                    <span className="muted object-version">v{obj.version}</span>
                  )}
                  {obj.status && (
                    <span className="badge" data-status={obj.status}>{obj.status}</span>
                  )}
                </div>
                <div className="object-fields">
                  {Object.entries(obj)
                    .filter(([k]) => !["object_id", "version", "status", "schema_version"].includes(k))
                    .map(([key, value]) => (
                      <div key={key} className="object-field">
                        <span className="object-field-label">{key.replace(/_/g, " ")}</span>
                        <span className="object-field-value">
                          {renderValue(value, membraneBypass, applyMembrane)}
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Links tab — link graph (V1 pattern) */}
      {tab === "links" && (
        <div className="link-list">
          {links.length === 0 ? (
            <div className="empty-state">{applyMembrane("No causal links in the register.", membraneBypass)}</div>
          ) : (
            links.map((link, i) => (
              <div key={i} className="link-edge">
                <span className="link-from">{link.from_object_version_id}</span>
                <span className="link-arrow">→</span>
                <span className="link-to">{link.to_object_version_id}</span>
                <span className={`badge`} data-status={link.active ? "active" : "inactive"}>
                  {applyMembrane(link.link_type, membraneBypass)}
                </span>
              </div>
            ))
          )}
        </div>
      )}

      {/* Causal spine tab — lamport ordering */}
      {tab === "spine" && (
        <div className="spine-list">
          {spine.length === 0 ? (
            <div className="empty-state">{applyMembrane("No causal spine data yet.", membraneBypass)}</div>
          ) : (
            spine.map((entry, i) => (
              <div key={i} className="spine-entry">
                <span className="spine-ordinal">L{entry.lamport}</span>
                <span className="spine-label">{applyMembrane(entry.label, membraneBypass)}</span>
                <span className={`badge`} data-status={entry.state?.toLowerCase()}>
                  {entry.state || "—"}
                </span>
              </div>
            ))
          )}
        </div>
      )}

      {/* Adjudication tab — F4 MoA verdicts */}
      {tab === "adjudication" && (
        <div className="adjudication-list">
          {f4Verdicts.length === 0 ? (
            <div className="empty-state">{applyMembrane("No F4 verdicts yet. The adjudicator runs when the frontier pipeline reaches F4.", membraneBypass)}</div>
          ) : (
            f4Verdicts.map((verdict: any, i: number) => {
              const v = verdict.verdict ?? verdict.value ?? 0;
              const status = v === 1 ? "eligible" : v === -1 ? "hold" : "parity";
              const icon = v === 1 ? "✓" : v === -1 ? "✗" : "○";
              return (
                <div key={i} className="card">
                  <div className="card-title">
                    {applyMembrane(verdict.label || verdict.page || "—", membraneBypass)}
                  </div>
                  <div className="receipt-meta">
                    <span className="badge" data-status={status === "eligible" ? "active" : status === "hold" ? "inactive" : "pending"}>
                      {icon} {status}
                    </span>
                  </div>
                  {verdict.note && <p className="muted" style={{ marginTop: 6 }}>{applyMembrane(verdict.note, membraneBypass)}</p>}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

// Schema-driven value renderer (from V1 renderField)
function renderValue(value: any, bypass: boolean, applyMembrane: (s: any, b: boolean) => any): React.ReactNode {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") {
    return <span className="badge" data-status={value ? "active" : "pending"}>{value ? "✓" : "✗"}</span>;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return "—";
    return (
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {value.map((item, i) => (
          <li key={i}>
            {typeof item === "object" ? JSON.stringify(item) : applyMembrane(String(item), bypass)}
          </li>
        ))}
      </ul>
    );
  }
  if (typeof value === "object") {
    return <pre style={{ fontSize: "0.78rem", color: "var(--muted)" }}>{JSON.stringify(value, null, 2)}</pre>;
  }
  return applyMembrane(String(value), bypass);
}
